<template>
    <ContentLoader v-slot :loading="item == null" class="artist-detail overflow-y-auto h-screen-header">
            <div class="artist-detail__backdrop" :style="artistBackdropStyle"></div>

            <section ref="heroContainer" class="artist-detail__hero">
                <div class="artist-detail__art-wrap">
                    <img
                        :src="item.image || '/static/dist/img/album_null.jpg'"
                        :alt="item.name"
                        class="artist-detail__art"
                    >
                </div>

                <div class="artist-detail__body">
                    <div class="artist-detail__headline">
                        <div class="artist-detail__kicker">{{ $tt('艺术家') }}</div>
                        <h1 class="artist-detail__title">{{ item.name }}</h1>
                        <div class="artist-detail__summary-line">
                            {{ $tt('artist.details.stats.summary', { albumCount: item.albumCount, trackCount: item.trackCount }) }}
                        </div>
                    </div>

                    <div class="artist-detail__stats" aria-label="artist metadata">
                        <div class="artist-detail__stat">
                            <span>{{ $tt('专辑') }}</span>
                            <strong>{{ item.albumCount || 0 }}</strong>
                        </div>
                        <div class="artist-detail__stat">
                            <span>{{ $tt('歌曲') }}</span>
                            <strong>{{ item.trackCount || 0 }}</strong>
                        </div>
                        <div class="artist-detail__stat">
                            <span>{{ $tt('风格') }}</span>
                            <strong>{{ genreSummary }}</strong>
                        </div>
                        <div class="artist-detail__stat">
                            <span>{{ $tt('相关') }}</span>
                            <strong>{{ item.similarArtist ? item.similarArtist.length : 0 }}</strong>
                        </div>
                    </div>

                    <p
                        v-if="item.description"
                        class="artist-detail__description"
                        :title="item.description"
                        @click="openArtistDescriptionModal"
                    >
                        {{ artistDescriptionPreview }}
                    </p>

                    <div v-if="item.genres && item.genres.length > 0" class="artist-detail__chips">
                        <router-link
                            v-for="genre in item.genres"
                            :key="genre"
                            class="artist-detail__chip"
                            :to="{ name: 'genre', params: { id: genre } }"
                        >
                            {{ genre }}
                        </router-link>
                    </div>

                    <div class="artist-detail__actions">
                        <button class="artist-detail__play" @click="playNow">
                            {{ $tt('播放') }}
                        </button>
                        <button class="artist-detail__action" @click="shuffleNow">
                            {{ $tt('随机') }}
                        </button>
                        <button class="artist-detail__action artist-detail__action--favorite" @click.stop="addFavouriteArtist">
                            {{ isFavourite ? $tt('已收藏') : $tt('收藏') }}
                        </button>
                        <button v-if="item.topTracks && item.topTracks.length > 0" class="artist-detail__action" @click="viewAll(item)">
                            {{ $tt('全部歌曲') }}
                        </button>
                        <label for="my-drawer-artist" class="artist-detail__action artist-detail__action--edit"
                            @click="handleEditArtist" v-if="isSuperuser">{{ $tt('编辑') }}</label>
                        <button class="artist-detail__action artist-detail__action--danger" onclick="artist_delete.showModal()"
                            v-if="isSuperuser">
                            {{ $tt('删除') }}
                        </button>
                    </div>
                </div>
            </section>

            <section ref="trackContainer" class="artist-detail__content">
                <template v-if="item.topTracks && item.topTracks.length > 0">
                    <div class="artist-detail__section artist-detail__section--tracks">
                        <div class="artist-detail__section-header">
                            <div>
                                <h2>{{ $tt('热门歌曲') }}</h2>
                                <p>{{ $tt('{count} 首歌曲', { count: item.topTracks.length }) }}</p>
                            </div>
                            <button class="artist-detail__link-button" @click="viewAll(item)">
                                {{ $tt('查看全部') }}
                            </button>
                        </div>
                        <TrackList :tracks="item.topTracks.slice(0, 5)" no-artist />
                    </div>
                </template>

                <template v-if="item.albums && item.albums.length > 0">
                    <div class="artist-detail__section">
                        <div class="artist-detail__section-header">
                            <div>
                                <h2>{{ $tt('最新专辑') }}</h2>
                                <p>{{ $tt('{count} 张专辑', { count: item.albums.length }) }}</p>
                            </div>
                        </div>
                        <AlbumList :items="item.albums.slice(0, 12)" small />
                    </div>
                </template>

                <template v-if="item.similarArtist && item.similarArtist.length > 0">
                    <div class="artist-detail__section">
                        <div class="artist-detail__section-header">
                            <div>
                                <h2>{{ $tt('相似艺术家') }}</h2>
                                <p>{{ $tt('{count} 位艺术家', { count: item.similarArtist.length }) }}</p>
                            </div>
                        </div>
                        <ArtistList :items="item.similarArtist" />
                    </div>
                </template>

                <div v-if="!hasContentSections" class="artist-detail__empty">
                    <div class="artist-detail__empty-title">{{ $tt('暂无可展示内容') }}</div>
                    <p>{{ $tt('可以先播放热门歌曲，或在编辑中补充艺人信息。') }}</p>
                </div>
            </section>
        <!-- 编辑艺术家信息抽屉 - 移到最外层避免 z-index 问题 -->
        <div class="drawer drawer-end" v-if="isSuperuser">
            <input id="my-drawer-artist" type="checkbox" class="drawer-toggle"
                v-model="isEditArtist" />
            <div class="drawer-side" style="z-index: 9999;">
                <label for="my-drawer-artist" aria-label="close sidebar"
                    class="drawer-overlay"></label>
                <div class="p-4 w-80 min-h-full bg-base-200 text-base-content">
                    <div class="font-medium text-xl mb-4">{{ $tt('编辑艺术家信息') }}</div>

                    <div class="mb-1">{{ $tt('艺术家:') }}</div>
                    <div>
                        <input type="text" v-model="artistFormData.name"
                            class="input input-bordered input-sm w-full max-w-xs" />
                    </div>
                    <div class="mb-1 mt-1">{{ $tt('艺术家封面:') }}</div>
                    <div>
                        <!-- 图片上传 -->
                        <div class="relative">
                            <!-- 图片预览区域 -->
                            <div
                                class="border-2 border-dashed border-gray-300 rounded-lg p-2 text-center hover:border-gray-400 transition-colors">
                                <div v-if="artistImagePreview" class="relative inline-block">
                                    <img :src="artistImagePreview" :alt="$tt('艺术家封面')"
                                        class="max-w-full max-h-48 rounded-lg shadow-md">
                                    <button @click.stop="removeArtistImage"
                                        class="absolute -top-2 -right-2 btn btn-circle btn-xs btn-error text-white hover:bg-red-600 z-50"
                                        type="button" style="pointer-events: auto;">
                                        ✕
                                    </button>
                                    <!-- 替换图片按钮 -->
                                    <button @click.stop="triggerArtistImageReplacement"
                                        class="absolute -bottom-2 -right-2 btn btn-circle btn-xs btn-neutral text-white z-50"
                                        type="button" style="pointer-events: auto;" :title="$tt('替换图片')">
                                        +
                                    </button>
                                </div>
                                <div v-else>
                                    <svg class="mx-auto h-12 w-12 text-gray-400"
                                        stroke="currentColor" fill="none" viewBox="0 0 48 48">
                                        <path
                                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                            stroke-width="2" stroke-linecap="round"
                                            stroke-linejoin="round" />
                                    </svg>
                                    <p class="mt-2 text-sm text-gray-500">{{ $tt('点击上传或拖拽图片到此处') }}</p>
                                    <p class="text-xs text-gray-400">{{ $tt('支持 JPG, PNG, GIF (最大 5MB)') }}</p>
                                </div>

                                <!-- 隐藏的文件输入 -->
                                <input ref="artistImageInput" type="file" accept="image/*"
                                    @change="handleArtistImageChange"
                                    class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                    :disabled="isUploadingArtistImage" v-show="!artistImagePreview">
                                <!-- 上传进度 -->
                                <div v-if="isUploadingArtistImage"
                                    class="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
                                    <div class="text-center">
                                        <span class="loading loading-spinner loading-md"></span>
                                        <p class="mt-2 text-sm text-gray-600">{{ $tt('上传中...') }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="mb-1 mt-1">{{ $tt('描述:') }}</div>
                    <textarea class="textarea textarea-bordered textarea-xs w-full max-w-xs h-48"
                        placeholder="." v-model="artistFormData.description"></textarea>
                    <button class="btn btn-neutral btn-sm btn-wide" @click="handleSave">{{ $tt('保存') }}</button>
                    <button class="btn btn-primary btn-sm btn-wide mt-2"
                        @click="handleSaveMetadata">{{ $tt('保存并更新音乐元数据') }}</button>
                    <div class="mt-4">{{ $tt('封面推荐:') }}</div>
                    <div v-for="(item2, index) in artistsData" :key="index">
                        <div class="flex justify-center items-center mt-1 mb-2">
                            <div>
                                <img :src="item2.artist_img"
                                    class="mask mask-squircle w-16 h-16 cursor-pointer object-contain"
                                    @click="handleCopy('artist_img', item2.artist_img)">
                            </div>
                            <div class="flex-1 ml-2">
                                <div class="cursor-pointer hover:underline"
                                    @click="handleCopy('name', item2.artist)">{{ item2.artist }}</div>
                                <div class="truncate w-56 text-gray-400 cursor-pointer hover:underline"
                                    @click="handleCopy('description', item2.desc)">{{ item2.desc }}
                                </div>
                            </div>
                        </div>
                        <hr>
                    </div>
                </div>
            </div>
        </div>

        <!-- Open the modal using ID.showModal() method -->
        <dialog id="my_modal_2" class="modal">
            <div class="modal-box">
                <h3 class="text-lg font-bold">{{ $tt('艺术家简介:') }}{{ item.name }}</h3>
                <div class="py-4">
                    <textarea class="textarea textarea-bordered textarea-xs w-full h-96" placeholder="."
                        v-model="item.description"></textarea>
                </div>
            </div>
            <form method="dialog" class="modal-backdrop">
                <button>close</button>
            </form>
        </dialog>
        <dialog id="artist_delete" class="modal modal-bottom sm:modal-middle">
            <div class="modal-box">
                <h3 class="text-lg font-bold">{{ $tt('删除艺术家') }}</h3>
                <div class="py-4 text-gray-400">
                    <div>{{ $tt('删除音乐: 将艺术家从音乐收藏中移除，同时删除艺术家名下的音乐文件。') }}</div>
                    <div>{{ $tt('删除收藏: 将艺术家从音乐收藏中移除，但并不会删除音乐文件。') }}</div>
                </div>
                <div class="modal-action">
                    <form method="dialog">
                        <!-- if there is a button in form, it will close the modal -->
                        <button class="btn btn-error text-white btn-sm mr-2" @click="handleDelete('delete_file')">{{ $tt('删除音乐') }}</button>
                        <button class="btn btn-neutral btn-sm mr-2" @click="handleDelete('delete_track')">{{ $tt('删除收藏') }}</button>
                        <button class="btn btn-sm">{{ $tt('取消') }}</button>
                    </form>
                </div>
            </div>
        </dialog>
    </ContentLoader>
</template>
<script>
import AlbumList from '@/views/library/album/AlbumList.vue'
import ArtistList from '@/views/library/artist/ArtistList.vue'
import TrackList from '@/views/library/track/TrackList.vue'
import ContentLoader from '@/components/library/ContentLoader'
import { mapGetters } from 'vuex'

export default {
    components: {
        ContentLoader,
        AlbumList,
        ArtistList,
        TrackList
    },
    data() {
        return {
            item: null,
            id: this.$route.params.id,
            customSettings: {
                isShow: false,
                title: this.$tt('艺术家详情')
            },
            artistsData: [],
            isFavourite: false,
            uploadHeader: [
                { name: 'X-CSRFToken', value: this.getCookie('django_vue_cli_csrftoken') },
                { name: 'AUTHORIZATION', value: this.getCookie('AUTHORIZATION') }
            ],
            uploadUrl: '/apimt/upload_image/',
            artistFormData: {},
            reloadImg: false,
            isEditArtist: false,
            // 新增的图片上传相关数据
            artistImagePreview: null,
            isUploadingArtistImage: false
        }
    },
    watch: {
        id: {
            immediate: true,
            async handler(value) {
                if (value !== undefined && value !== null && value !== '' && value !== 'undefined' && value !== 'null') {
                    this.item = null
                    this.item = await this.$api.subsonic.getArtistDetails(value)
                    this.isFavourite = this.item.favourite
                }       
            }
        }
    },
    computed: {
        ...mapGetters({
            isSuperuser: 'getIsSuperuser'
        }),
        genreSummary() {
            if (!this.item?.genres || this.item.genres.length === 0) {
                return this.$tt('未分类')
            }

            return this.item.genres.slice(0, 2).join(' / ')
        },
        artistDescriptionPreview() {
            if (!this.item?.description) return ''
            return this.item.description.length > 160 ? `${this.item.description.slice(0, 160)}...` : this.item.description
        },
        artistBackdropStyle() {
            return {
                backgroundImage: `url('${this.item?.image || '/static/dist/img/album_null.jpg'}')`
            }
        },
        hasContentSections() {
            return Boolean(
                (this.item?.topTracks && this.item.topTracks.length > 0) ||
                (this.item?.albums && this.item.albums.length > 0) ||
                (this.item?.similarArtist && this.item.similarArtist.length > 0)
            )
        }
    },
    methods: {
        playNow() {
            return this.$store.dispatch('playTrackList', {
                tracks: this.item.topTracks
            })
        },
        shuffleNow() {
            return this.$store.dispatch('player/shuffleNow', {
                tracks: this.item.topTracks
            })
        },
        toggleFavourite() {
            return this.favouriteStore.toggle('artist', this.id)
        },
        handleEditArtist() {
            if (this.item.image) {
                // 设置图片预览
                this.artistImagePreview = this.item.image
            }
            this.artistFormData = {
                name: this.item.name,
                description: this.item.description,
                artist_img: this.item.image
            }
            this.$api.Task.fetchArtists({
                title: this.item.name,
                resource: 'kuwo',
                full_path: ''
            }).then((res) => {
                this.artistsData = res.data
            })
        },
        handleSave() {
            this.$api.Task.postArtist({
                id: this.id,
                artist_img: this.artistFormData.artist_img,
                description: this.artistFormData.description,
                name: this.artistFormData.name
            }).then((res) => {
                if (res.result) {
                    this.customSettings.isShow = false
                    this.$myMsg.notify({
                        title: this.$tt('修改成功！'),
                        type: 'success',
                        time: 2000,
                    })
                    this.$api.subsonic.getArtistDetails(this.id).then((res) => {
                        this.item = res
                    })
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('修改失败！'),
                        type: 'error',
                        time: 2000,
                    })
                }
            })
        },
        handleSaveMetadata() {
            this.$api.Task.postArtist({
                id: this.id,
                artist_img: this.artistFormData.artist_img,
                description: this.artistFormData.description,
                name: this.artistFormData.name,
                write_metadata: true
            }).then((res) => {
                if (res.result) {
                    this.customSettings.isShow = false
                    this.$myMsg.notify({
                        title: this.$tt('修改成功！'),
                        type: 'success',
                        time: 2000,
                    })
                    this.$api.subsonic.getArtistDetails(this.id).then((res) => {
                        this.item = res
                    })
                    this.isEditArtist = false
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('修改失败！'),
                        type: 'error',
                        time: 2000,
                    })
                }
            })
        },
        handleDelete(mode) {
            this.$api.Task.deleteArtist({ 'id': this.id, mode }).then((res) => {
                if (res.result) {
                    this.$myMsg.notify({
                        title: this.$tt('删除成功！'),
                        type: 'success',
                        time: 2000,
                    })
                    this.$router.push({ path: '/artists/most-albums' })
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('删除失败！'),
                        type: 'error',
                        time: 2000,
                    })
                }
            })
        },
        addFavouriteArtist() {
            if (this.isFavourite) {
                this.$api.subsonic.removeFavourite(this.item.id, 'artist').then((res) => {
                    this.isFavourite = false
                })
            } else {
                this.$api.subsonic.addFavourite(this.item.id, 'artist').then((res) => {
                    this.isFavourite = true
                })
            }
        },
        // 渲染图片
        handleRes(response) {
            if (response.result) {
                this.artistFormData.artist_img = response.data
                return true
            } else {
                return false
            }
        },
        // 移除艺术家封面
        removeArtistImage() {
            this.artistImagePreview = null
            this.artistFormData.artist_img = ''
            if (this.$refs.artistImageInput) {
                this.$refs.artistImageInput.value = ''
            }
        },
        // 触发艺术家封面替换
        triggerArtistImageReplacement() {
            // 直接创建临时input，更可靠
            this.createTempFileInput('artist')
        },

        // 创建临时文件输入
        createTempFileInput(type) {
            const input = document.createElement('input')
            input.type = 'file'
            input.accept = 'image/*'
            input.style.display = 'none'
            input.onchange = (event) => {
                if (type === 'artist') {
                    this.handleArtistImageChange(event)
                }
                // 清理临时元素
                if (input.parentNode) {
                    input.parentNode.removeChild(input)
                }
            }
            document.body.appendChild(input)
            input.click()
        },
        // 处理艺术家封面图片选择
        handleArtistImageChange(event) {
            const file = event.target.files[0]
            if (!file) return

            // 验证文件类型
            if (!file.type.startsWith('image/')) {
                this.$myMsg.notify({
                    title: this.$tt('文件类型错误'),
                    content: this.$tt('请选择图片文件'),
                    type: 'error',
                    time: 2000,
                })
                return
            }

            // 验证文件大小 (5MB)
            if (file.size > 5 * 1024 * 1024) {
                this.$myMsg.notify({
                    title: this.$tt('文件过大'),
                    content: this.$tt('图片大小不能超过5MB'),
                    type: 'error',
                    time: 2000,
                })
                return
            }

            // 显示预览
            const reader = new FileReader()
            reader.onload = (e) => {
                this.artistImagePreview = e.target.result
            }
            reader.readAsDataURL(file)

            // 上传图片
            this.uploadImage(file, 'artist')
        },
        // 通用图片上传方法
        uploadImage(file, type) {
            const formData = new FormData()
            formData.append('upload_file', file)

            // 设置上传状态
            if (type === 'artist') {
                this.isUploadingArtistImage = true
            }

            // 创建 XMLHttpRequest
            const xhr = new XMLHttpRequest()

            // 设置请求头
            xhr.open('POST', this.uploadUrl, true)
            xhr.setRequestHeader('X-CSRFToken', this.getCookie('django_vue_cli_csrftoken'))
            xhr.setRequestHeader('AUTHORIZATION', this.getCookie('AUTHORIZATION'))
            xhr.withCredentials = true

            // 处理响应
            xhr.onload = () => {
                // 重置上传状态
                if (type === 'artist') {
                    this.isUploadingArtistImage = false
                }

                if (xhr.status === 200) {
                    try {
                        const response = JSON.parse(xhr.responseText)
                        if (this.handleRes(response)) {
                            this.$myMsg.notify({
                                title: this.$tt('上传成功'),
                                type: 'success',
                                time: 2000,
                            })
                        } else {
                            this.$myMsg.notify({
                                title: this.$tt('上传失败'),
                                content: response.message || this.$tt('未知错误'),
                                type: 'error',
                                time: 2000,
                            })
                        }
                    } catch (e) {
                        console.log(e)
                        this.$myMsg.notify({
                            title: this.$tt('上传失败'),
                            content: this.$tt('服务器响应格式错误'),
                            type: 'error',
                            time: 2000,
                        })
                    }
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('上传失败'),
                        content: this.$tt('HTTP错误: {status}', { status: xhr.status }),
                        type: 'error',
                        time: 2000,
                    })
                }
            }

            // 处理错误
            xhr.onerror = () => {
                if (type === 'artist') {
                    this.isUploadingArtistImage = false
                }
                this.$myMsg.notify({
                    title: this.$tt('上传失败'),
                    content: this.$tt('网络错误'),
                    type: 'error',
                    time: 2000,
                })
            }

            // 发送请求
            xhr.send(formData)
        },
        handleCopy(k, v) {
            if (k === 'artist_img') {
                this.files1 = [
                    {
                        name: 'cover.png',
                        status: 'done',
                        url: v
                    }
                ]
                this.artistFormData.artist_img = v
                // 设置图片预览
                this.artistImagePreview = v
            } else {
                this.artistFormData[k] = v
            }
        },
        viewAll(item) {
            this.$router.push({ path: `/artist/tracks/${item.id}`, query: { name: item.name } })
        },
        openArtistDescriptionModal() {
            const modal = document.getElementById('my_modal_2')
            if (modal && typeof modal.showModal === 'function') {
                modal.showModal()
            }
        }
    }
}
</script>

<style scoped>
.artist-detail {
    position: relative;
    height: calc(100vh - 5rem);
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 18px;
    box-sizing: border-box;
    border-radius: 1rem;
    color: oklch(var(--bc));
    background:
        radial-gradient(circle at 8% 0%, oklch(var(--p) / 0.15), transparent 34rem),
        radial-gradient(circle at 92% 12%, oklch(var(--a) / 0.11), transparent 28rem),
        linear-gradient(135deg, oklch(var(--b1)), oklch(var(--b2)));
}

.artist-detail__backdrop {
    position: absolute;
    inset: -2rem;
    background-position: center 24%;
    background-size: cover;
    filter: blur(48px) saturate(1.08);
    opacity: 0.15;
    transform: scale(1.06);
    pointer-events: none;
}

.artist-detail__hero,
.artist-detail__content {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 1480px;
    margin-left: auto;
    margin-right: auto;
    border: 1px solid oklch(var(--bc) / 0.08);
    background: oklch(var(--b1) / 0.76);
    box-shadow: 0 18px 46px oklch(var(--bc) / 0.08);
    backdrop-filter: blur(18px);
}

.artist-detail__hero {
    display: grid;
    grid-template-columns: minmax(10rem, 15rem) minmax(0, 1fr);
    gap: 1.3rem;
    align-items: center;
    padding: 1rem;
    border-radius: 1.3rem;
    transition: padding 0.28s ease, grid-template-columns 0.28s ease;
}

.artist-detail__art-wrap {
    width: 100%;
    aspect-ratio: 1;
    padding: 0.45rem;
    border-radius: 1.05rem;
    background: linear-gradient(180deg, oklch(var(--b1) / 0.9), oklch(var(--b2) / 0.72));
    box-shadow: 0 22px 40px oklch(var(--bc) / 0.12);
}

.artist-detail__art {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0.82rem;
}

.artist-detail__body {
    min-width: 0;
    display: grid;
    gap: 0.75rem;
}

.artist-detail__headline {
    min-width: 0;
}

.artist-detail__kicker {
    font-size: 0.78rem;
    font-weight: 800;
    color: oklch(var(--bc) / 0.52);
}

.artist-detail__title {
    margin: 0.2rem 0 0;
    max-width: 18ch;
    font-size: clamp(1.7rem, 4vw, 3.4rem);
    line-height: 0.96;
    font-weight: 850;
    letter-spacing: 0;
    color: oklch(var(--bc));
    word-break: break-word;
}

.artist-detail__summary-line {
    max-width: 100%;
    margin-top: 0.5rem;
    color: oklch(var(--bc) / 0.66);
    font-size: 0.98rem;
    font-weight: 700;
}

.artist-detail__stats {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.55rem;
    max-width: 42rem;
}

.artist-detail__stat {
    min-width: 0;
    padding: 0.72rem 0.82rem;
    border-radius: 0.9rem;
    background: oklch(var(--b2) / 0.78);
}

.artist-detail__stat span,
.artist-detail__section-header p,
.artist-detail__empty p {
    display: block;
    color: oklch(var(--bc) / 0.54);
    font-size: 0.76rem;
    font-weight: 700;
}

.artist-detail__stat strong {
    display: block;
    margin-top: 0.2rem;
    color: oklch(var(--bc));
    font-size: 1rem;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.artist-detail__description {
    max-width: 70ch;
    margin: 0;
    color: oklch(var(--bc) / 0.64);
    font-size: 0.92rem;
    line-height: 1.55;
    cursor: pointer;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.artist-detail__description:hover {
    color: oklch(var(--p));
}

.artist-detail__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
}

.artist-detail__chip {
    min-width: 0;
    max-width: 12rem;
    min-height: 2rem;
    display: inline-flex;
    align-items: center;
    border: 0;
    border-radius: 999px;
    padding: 0.42rem 0.78rem;
    background: oklch(var(--b2) / 0.9);
    color: oklch(var(--bc) / 0.72);
    font-size: 0.78rem;
    font-weight: 800;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.artist-detail__chip:hover {
    color: oklch(var(--p));
}

.artist-detail__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
}

.artist-detail__play,
.artist-detail__action {
    min-height: 2.55rem;
    border: 0;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    padding: 0 1rem;
    font-size: 0.86rem;
    font-weight: 850;
    white-space: nowrap;
    cursor: pointer;
    transition: transform 0.18s ease, background 0.18s ease, color 0.18s ease;
}

.artist-detail__play:active,
.artist-detail__action:active {
    transform: translateY(1px) scale(0.99);
}

.artist-detail__play {
    background: oklch(var(--n));
    color: oklch(var(--nc));
    box-shadow: 0 14px 28px oklch(var(--bc) / 0.18);
}

.artist-detail__action {
    background: oklch(var(--b2) / 0.94);
    color: oklch(var(--bc) / 0.78);
}

.artist-detail__action:hover,
.artist-detail__link-button:hover {
    color: oklch(var(--p));
}

.artist-detail__action--danger {
    color: oklch(var(--er));
}

.artist-detail__content {
    margin-top: 0.85rem;
    border-radius: 1.15rem;
    padding: 0.85rem;
}

.artist-detail__section + .artist-detail__section {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid oklch(var(--bc) / 0.08);
}

.artist-detail__section--tracks {
    border: 1px solid oklch(var(--bc) / 0.08);
    border-radius: 1.15rem;
    overflow: hidden;
    padding: 0.85rem;
    background: oklch(var(--b1) / 0.72);
}

.artist-detail__section-header {
    position: sticky;
    top: -0.85rem;
    z-index: 6;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin: -0.85rem -0.85rem 0.65rem;
    padding: 0.85rem;
    background: oklch(var(--b1) / 0.9);
    border-bottom: 1px solid oklch(var(--bc) / 0.06);
    backdrop-filter: blur(16px);
}

.artist-detail__section--tracks .artist-detail__section-header {
    margin: -0.85rem -0.85rem 0.65rem;
}

.artist-detail__section:not(:first-child) .artist-detail__section-header {
    position: static;
    margin: 0 0 0.65rem;
    padding: 0;
    background: transparent;
    border-bottom: 0;
    backdrop-filter: none;
}

.artist-detail__section-header h2 {
    margin: 0;
    color: oklch(var(--bc));
    font-size: 1rem;
    font-weight: 850;
}

.artist-detail__section-header p {
    margin: 0.16rem 0 0;
}

.artist-detail__link-button {
    min-height: 2.1rem;
    border: 0;
    border-radius: 999px;
    padding: 0 0.82rem;
    background: oklch(var(--b2) / 0.94);
    color: oklch(var(--bc) / 0.74);
    font-size: 0.78rem;
    font-weight: 850;
    white-space: nowrap;
}

.artist-detail__empty {
    min-height: 14rem;
    display: grid;
    place-content: center;
    gap: 0.35rem;
    text-align: center;
}

.artist-detail__empty-title {
    color: oklch(var(--bc));
    font-size: 1rem;
    font-weight: 850;
}

.artist-detail__empty p {
    margin: 0;
}

@media (prefers-reduced-motion: reduce) {
    .artist-detail__hero,
    .artist-detail__play,
    .artist-detail__action,
    .artist-detail__link-button {
        transition: none;
    }
}

@media (max-width: 768px) {
    .artist-detail {
        padding: 0.85rem;
        border-radius: 0;
    }

    .artist-detail__hero {
        grid-template-columns: 5.75rem minmax(0, 1fr);
        gap: 0.85rem;
        align-items: start;
        padding: 0.75rem;
        border-radius: 1.35rem;
    }

    .artist-detail__body {
        gap: 0.55rem;
    }

    .artist-detail__kicker,
    .artist-detail__description,
    .artist-detail__chips,
    .artist-detail__stats {
        display: none;
    }

    .artist-detail__title {
        max-width: none;
        font-size: clamp(1.25rem, 7vw, 1.85rem);
        line-height: 1.04;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .artist-detail__summary-line {
        margin-top: 0.35rem;
        font-size: 0.86rem;
    }

    .artist-detail__actions {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 0.45rem;
    }

    .artist-detail__play,
    .artist-detail__action {
        min-width: 0;
        min-height: 2.35rem;
        padding: 0 0.55rem;
        font-size: 0.76rem;
    }

    .artist-detail__content {
        margin-top: 0.65rem;
        padding: 0.65rem;
        border-radius: 1.05rem;
    }

    .artist-detail__section-header {
        top: -0.65rem;
        margin: -0.65rem -0.65rem 0.55rem;
        padding: 0.65rem;
    }

    .artist-detail__section--tracks {
        border-radius: 1.05rem;
        padding: 0.65rem;
    }

    .artist-detail__section--tracks .artist-detail__section-header {
        margin: -0.65rem -0.65rem 0.55rem;
    }

    .artist-detail__section:not(:first-child) .artist-detail__section-header {
        margin: 0 0 0.55rem;
        padding: 0;
    }
}

@media (max-width: 420px) {
    .artist-detail__actions {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
